import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BRAND } from '@fin/shared/brand';
import { dubaiLocalToISO, isoToDubaiLocal } from '@fin/shared/time';
import { JOB_PRIORITIES, SERVICE_LABELS, STATUS_LABELS } from '@fin/shared/enums';
import { buildWhatsAppLink } from '@fin/shared/whatsapp';
import { Button, Card, Field, Modal, PageHeader, inputClass, useDisclosure } from '../components/ui';
import {
  useCustomers,
  useJob,
  useJobLog,
  useJobProof,
  usePatchJob,
  useWorkers,
  useJobInvoices,
  useCreateInvoice,
  fetchProofForShare,
  createShareLinks,
  fetchInvoiceShareUrl,
  type JobLogEntry,
  type JobProof,
  type Invoice,
  type InvoiceCreateBody,
} from '../lib/queries';

const TZ = 'Asia/Dubai';

function fmt(iso: string | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AE', {
    timeZone: TZ,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Like fmt() but renders "<date> · Anytime" when the job has no time slot. */
function fmtSchedule(iso: string | undefined, anytime?: boolean): string {
  if (!iso) return '—';
  if (anytime) {
    const date = new Date(iso).toLocaleDateString('en-AE', { timeZone: TZ, dateStyle: 'medium' });
    return `${date} · Anytime`;
  }
  return fmt(iso);
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const job = useJob(id);
  const log = useJobLog(id);
  const proof = useJobProof(id);
  const customers = useCustomers();
  const workers = useWorkers();
  const patch = usePatchJob();
  const [editing, setEditing] = useState(false);
  const reschedule = useDisclosure();
  const shareMedia = useDisclosure();
  const createInvoice = useDisclosure();

  const cust = job.data && (customers.data ?? []).find((c) => c.id === job.data!.customerId);
  const workerById = Object.fromEntries((workers.data ?? []).map((w) => [w.id, w]));

  return (
    <>
      <PageHeader
        title="Job"
        action={
          <div className="flex items-center gap-2">
            <Link to="/jobs" className="text-sm text-stone-600 hover:underline">
              ← Back to jobs
            </Link>
            {job.data && (
              <Button onClick={() => setEditing((v) => !v)}>
                {editing ? 'Cancel' : 'Edit'}
              </Button>
            )}
          </div>
        }
      />

      {job.isPending && <p className="text-stone-500">Loading…</p>}
      {job.isError && (
        <p style={{ color: BRAND.danger }}>Failed to load: {(job.error as Error).message}</p>
      )}
      {job.data && editing && (
        <EditJobForm
          initial={job.data}
          workers={workers.data ?? []}
          submitting={patch.isPending}
          onCancel={() => setEditing(false)}
          onSave={async (body) => {
            await patch.mutateAsync({ id: job.data!.id, body });
            setEditing(false);
          }}
        />
      )}
      {job.data && !editing && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {job.data.status === 'on_hold' && job.data.holdReason && (
            <div className="lg:col-span-2 rounded-lg border-l-4 bg-white p-4 shadow-sm" style={{ borderLeftColor: '#A16207' }}>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#A16207' }}>
                ⏸ Worker put job on hold
              </p>
              <p className="mt-1 whitespace-pre-line text-sm text-stone-800">{job.data.holdReason}</p>
              {job.data.holdAt && (
                <p className="mt-1 text-[11px] text-stone-500">
                  {new Date(job.data.holdAt).toLocaleString('en-AE', { timeZone: 'Asia/Dubai', dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              )}
            </div>
          )}
          <Card className="p-5 lg:col-span-2">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-500">
              Status actions
            </h2>
            <StatusActions
              status={job.data.status}
              disabled={patch.isPending}
              onReschedule={job.data.status === 'on_hold' ? reschedule.onOpen : undefined}
              onChange={async (status, reason) => {
                const body: Record<string, unknown> = { status };
                if (reason) body.reason = reason;
                await patch.mutateAsync({ id: job.data!.id, body });
              }}
            />
          </Card>
          <Card className="p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-500">
              Job
            </h2>
            <dl className="grid grid-cols-3 gap-y-2 text-sm">
              <Row label="ID">{job.data.id}</Row>
              <Row label="Status">
                <span
                  className="rounded-full px-2 py-0.5 text-xs"
                  style={{ backgroundColor: BRAND.primary, color: 'white' }}
                >
                  {STATUS_LABELS[job.data.status]?.en ?? job.data.status}
                </span>
              </Row>
              <Row label="Service">
                {SERVICE_LABELS[job.data.serviceType as keyof typeof SERVICE_LABELS]?.en ??
                  job.data.serviceType}
              </Row>
              <Row label="Priority">{job.data.priority}</Row>
              <Row label="Scheduled">{fmtSchedule(job.data.scheduledAt, job.data.anytime)}</Row>
              <Row label="Estimated">{job.data.estimatedDurationMinutes} min</Row>
              <Row label="Created">{fmt(job.data.createdAt)}</Row>
              <Row label="Updated">{fmt(job.data.updatedAt)}</Row>
              {job.data.description && (
                <Row label="Description">
                  <span className="whitespace-pre-wrap">{job.data.description}</span>
                </Row>
              )}
              {job.data.internalNotes && (
                <Row label="Internal notes">
                  <span className="whitespace-pre-wrap text-stone-600">
                    {job.data.internalNotes}
                  </span>
                </Row>
              )}
              {job.data.workerNotes && (
                <Row label="Worker notes">
                  <span className="whitespace-pre-wrap text-stone-600">
                    {job.data.workerNotes}
                  </span>
                </Row>
              )}
            </dl>
          </Card>

          <Card className="p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-500">
              People
            </h2>
            <dl className="grid grid-cols-3 gap-y-2 text-sm">
              <Row label="Customer">
                {cust ? (
                  <>
                    <div className="font-medium">{cust.name}</div>
                    <div className="text-stone-500">{cust.phone}</div>
                    <div className="text-stone-500">
                      {cust.address.area}, {cust.address.emirate.replace('_', ' ')}
                    </div>
                  </>
                ) : (
                  '—'
                )}
              </Row>
              <Row label="Worker">
                {(() => {
                  const ids = job.data!.assignedWorkerIds && job.data!.assignedWorkerIds.length > 0
                    ? job.data!.assignedWorkerIds
                    : job.data!.assignedWorkerId
                      ? [job.data!.assignedWorkerId]
                      : [];
                  if (ids.length === 0) return 'Unassigned';
                  const names = ids.map((wid, i) => {
                    const w = workerById[wid];
                    const label = w ? w.name : wid.slice(-6);
                    return i === 0 ? `${label} (primary)` : label;
                  });
                  return names.join(', ');
                })()}
              </Row>
            </dl>
          </Card>

          <Card className="p-5 lg:col-span-2">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-500">
                Worker proof &amp; signature
              </h2>
              {(proof.data ?? []).length > 0 && cust && (
                <Button variant="white" onClick={shareMedia.onOpen}>
                  📤 Send to customer WhatsApp
                </Button>
              )}
            </div>
            <ProofGallery items={proof.data ?? []} loading={proof.isPending} error={proof.error as Error | null} />
          </Card>

          <Card className="p-5 lg:col-span-2">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-500">
                Invoices
              </h2>
              {cust && (
                <Button variant="white" onClick={createInvoice.onOpen}>
                  + Create invoice
                </Button>
              )}
            </div>
            <InvoicesPanel
              jobId={job.data!.id}
              customerName={cust?.name}
              customerPhone={cust?.whatsappPhone ?? cust?.phone}
              serviceType={SERVICE_LABELS[job.data!.serviceType as keyof typeof SERVICE_LABELS]?.en ?? job.data!.serviceType}
            />
          </Card>

          <Card className="p-5 lg:col-span-2">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-500">
              Activity log
            </h2>
            {log.isPending && <p className="text-stone-500 text-sm">Loading…</p>}
            {log.data && log.data.length === 0 && (
              <p className="text-stone-500 text-sm">No changes recorded yet.</p>
            )}
            {log.data && log.data.length > 0 && (
              <ul className="space-y-2 text-sm">
                {log.data.map((e) => (
                  <li
                    key={e.id}
                    className="rounded border border-stone-200 bg-stone-50 px-3 py-2"
                  >
                    <div className="flex justify-between text-xs text-stone-500">
                      <span>{e.actorEmail || e.actorId}</span>
                      <span>{fmt(e.at)}</span>
                    </div>
                    <div className="mt-1">{describe(e, workerById)}</div>
                    {e.reason && (
                      <div className="mt-1 text-xs italic text-stone-600">“{e.reason}”</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {job.data && (
        <RescheduleModal
          open={reschedule.open}
          onClose={reschedule.onClose}
          job={job.data}
          workers={workers.data ?? []}
          submitting={patch.isPending}
          onSave={async (body) => {
            await patch.mutateAsync({ id: job.data!.id, body });
            reschedule.onClose();
          }}
        />
      )}

      {job.data && cust && (
        <ShareMediaModal
          open={shareMedia.open}
          onClose={shareMedia.onClose}
          jobId={job.data.id}
          serviceType={SERVICE_LABELS[job.data.serviceType as keyof typeof SERVICE_LABELS]?.en ?? job.data.serviceType}
          customerName={cust.name}
          customerPhone={cust.whatsappPhone ?? cust.phone}
        />
      )}

      {job.data && cust && (
        <CreateInvoiceModal
          open={createInvoice.open}
          onClose={createInvoice.onClose}
          jobId={job.data.id}
        />
      )}
    </>
  );
}

function describe(
  e: JobLogEntry,
  workerById: Record<string, { name: string } | undefined>,
): string {
  switch (e.action) {
    case 'status':
      return `Status changed: ${e.from ?? '—'} → ${e.to ?? '—'}`;
    case 'assigned_worker': {
      const lookup = (csv?: string) =>
        csv
          ? csv
              .split(',')
              .filter(Boolean)
              .map((id) => workerById[id]?.name ?? id.slice(-6))
              .join(', ')
          : 'unassigned';
      return `Assigned worker: ${lookup(e.from)} → ${lookup(e.to)}`;
    }
    case 'scheduled_at':
      return `Rescheduled: ${e.from ? fmt(e.from) : '—'} → ${e.to ? fmt(e.to) : '—'}`;
    default:
      return e.action;
  }
}

const KIND_LABELS: Record<JobProof['kind'], string> = {
  before: 'Before',
  after: 'After',
  parts: 'Parts',
  signature: 'Signature',
  video: 'Video',
  intake_photo: 'Customer photo',
  intake_video: 'Customer video',
};
const KIND_ORDER: JobProof['kind'][] = ['intake_photo', 'intake_video', 'before', 'after', 'parts', 'video', 'signature'];

function ProofGallery({
  items,
  loading,
  error,
}: {
  items: JobProof[];
  loading: boolean;
  error: Error | null;
}) {
  if (loading) return <p className="text-sm text-stone-500">Loading photos…</p>;
  if (error) return <p className="text-sm" style={{ color: BRAND.danger }}>Failed to load photos: {error.message}</p>;
  if (items.length === 0)
    return (
      <div className="rounded-lg border-2 border-dashed border-stone-200 bg-stone-50 px-4 py-8 text-center text-sm text-stone-500">
        No photos uploaded yet. The assigned worker will upload before/after photos and a customer signature from the worker app.
      </div>
    );

  const grouped = KIND_ORDER.map((k) => ({ kind: k, items: items.filter((i) => i.kind === k) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-5">
      {grouped.map((g) => (
        <div key={g.kind}>
          <div className="mb-2 flex items-center gap-2">
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
              style={{
                backgroundColor: g.kind === 'signature' ? BRAND.primary : BRAND.surfaceMuted,
                color: g.kind === 'signature' ? 'white' : BRAND.text,
              }}
            >
              {KIND_LABELS[g.kind]}
            </span>
            <span className="text-xs text-stone-500">
              {g.items.length}
              {' '}
              {g.kind === 'video' || g.kind === 'intake_video'
                ? g.items.length === 1 ? 'video' : 'videos'
                : g.items.length === 1 ? 'photo' : 'photos'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {g.items.map((p) => {
              const isVideo = p.kind === 'video' || p.kind === 'intake_video';
              if (isVideo) {
                return (
                  <div
                    key={p.id}
                    className="group relative block overflow-hidden rounded-lg border border-stone-200 bg-black shadow-sm"
                    title={`${KIND_LABELS[p.kind]} · ${fmt(p.uploadedAt)}`}
                  >
                    <video
                      src={p.url}
                      controls
                      preload="metadata"
                      playsInline
                      className="aspect-square w-full bg-black object-contain"
                    />
                    <div className="pointer-events-none absolute left-1.5 top-1.5 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                      🎥 {KIND_LABELS[p.kind]}
                    </div>
                    {p.processing && (
                      <div className="pointer-events-none absolute inset-x-1.5 bottom-1.5 rounded bg-amber-500/90 px-1.5 py-1 text-center text-[10px] font-semibold text-white">
                        Black screen? Tap ⬇ to download &amp; play
                      </div>
                    )}
                    <div className="absolute right-1.5 top-1.5 flex gap-1">
                      <a
                        href={p.originalUrl ?? p.url}
                        download
                        className="rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-black/85"
                        title="Download original"
                      >
                        ⬇
                      </a>
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-black/85"
                        title="Open in new tab"
                      >
                        ↗
                      </a>
                    </div>
                  </div>
                );
              }
              return (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative block overflow-hidden rounded-lg border border-stone-200 bg-stone-50 shadow-sm transition hover:shadow-md"
                  title={`${KIND_LABELS[p.kind]} · ${fmt(p.uploadedAt)}`}
                >
                  <img
                    src={p.url}
                    alt={`${KIND_LABELS[p.kind]} photo uploaded ${fmt(p.uploadedAt)}`}
                    loading="lazy"
                    className="aspect-square w-full object-cover transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-[10px] text-white opacity-0 transition group-hover:opacity-100">
                    {fmt(p.uploadedAt)}
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-stone-500">{label}</dt>
      <dd className="col-span-2 text-stone-800">{children}</dd>
    </>
  );
}

interface JobInitial {
  id: string;
  scheduledAt: string;
  anytime?: boolean;
  estimatedDurationMinutes: number;
  priority: string;
  description?: string;
  internalNotes?: string;
  assignedWorkerId?: string;
  assignedWorkerIds?: string[];
}

// datetime-local must read/write Asia/Dubai wall time (the operating zone),
// not the admin's browser timezone — otherwise the stored time is offset and
// the worker sees the wrong slot.
function toLocalInput(iso: string): string {
  return isoToDubaiLocal(iso);
}

function EditJobForm({
  initial,
  workers,
  submitting,
  onCancel,
  onSave,
}: {
  initial: JobInitial;
  workers: { id: string; name: string; active: boolean }[];
  submitting: boolean;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(initial.scheduledAt));
  const [anytime, setAnytime] = useState(initial.anytime ?? false);
  const [duration, setDuration] = useState(initial.estimatedDurationMinutes);
  const [priority, setPriority] = useState<string>(initial.priority);
  const [description, setDescription] = useState(initial.description ?? '');
  const [internalNotes, setInternalNotes] = useState(initial.internalNotes ?? '');
  const [assignedWorkerIds, setAssignedWorkerIds] = useState<string[]>(
    initial.assignedWorkerIds && initial.assignedWorkerIds.length > 0
      ? initial.assignedWorkerIds
      : initial.assignedWorkerId
        ? [initial.assignedWorkerId]
        : [],
  );
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setScheduledAt(toLocalInput(initial.scheduledAt));
  }, [initial.scheduledAt]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const localValue = anytime ? `${scheduledAt.slice(0, 10)}T08:00` : scheduledAt;
      const body: Record<string, unknown> = {
        scheduledAt: dubaiLocalToISO(localValue),
        anytime,
        estimatedDurationMinutes: Number(duration),
        priority,
        description: description || undefined,
        internalNotes: internalNotes || undefined,
      };
      const initialIds =
        initial.assignedWorkerIds && initial.assignedWorkerIds.length > 0
          ? initial.assignedWorkerIds
          : initial.assignedWorkerId
            ? [initial.assignedWorkerId]
            : [];
      const sameAssignment =
        initialIds.length === assignedWorkerIds.length &&
        initialIds.every((id, i) => id === assignedWorkerIds[i]);
      if (!sameAssignment) {
        body['assignedWorkerIds'] = assignedWorkerIds;
      }
      if (reason.trim()) body['reason'] = reason.trim();
      await onSave(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Card className="p-5">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {error && (
          <div
            role="alert"
            className="rounded border px-3 py-2 text-xs"
            style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
          >
            {error}
          </div>
        )}
        <label
          className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2.5"
          style={{
            borderColor: anytime ? BRAND.primary : BRAND.border,
            backgroundColor: anytime ? '#F0FDF4' : 'white',
          }}
        >
          <span>
            <span className="text-sm font-medium" style={{ color: BRAND.text }}>Anytime</span>
            <span className="block text-xs text-stone-500">
              Customer is flexible / home all day — no specific time slot
            </span>
          </span>
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={anytime}
            onChange={(e) => setAnytime(e.target.checked)}
          />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={anytime ? 'Day' : 'When'} hint={anytime ? 'Pick the day — the time is ignored.' : undefined}>
            <input
              className={inputClass}
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              required
            />
          </Field>
          <Field label="Estimated duration (min)">
            <input
              className={inputClass}
              type="number"
              min={15}
              max={720}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Priority">
            <select
              className={inputClass}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {JOB_PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Assigned workers"
            hint={`First selected = primary. Click again to remove. Max 5.${assignedWorkerIds.length > 0 ? ` ${assignedWorkerIds.length} selected.` : ''}`}
          >
            <div className="flex flex-wrap gap-1.5">
              {workers
                .filter((w) => w.active || assignedWorkerIds.includes(w.id))
                .map((w) => {
                  const idx = assignedWorkerIds.indexOf(w.id);
                  const selected = idx !== -1;
                  const isPrimary = idx === 0;
                  const atCap = !selected && assignedWorkerIds.length >= 5;
                  return (
                    <button
                      key={w.id}
                      type="button"
                      disabled={atCap}
                      onClick={() => {
                        setAssignedWorkerIds((cur) =>
                          selected
                            ? cur.filter((id) => id !== w.id)
                            : cur.length >= 5
                              ? cur
                              : [...cur, w.id],
                        );
                      }}
                      className="rounded-full px-3 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                      style={{
                        backgroundColor: selected ? BRAND.primary : 'white',
                        color: selected ? 'white' : BRAND.text,
                        border: `1px solid ${selected ? BRAND.primary : BRAND.border}`,
                      }}
                      title={isPrimary ? 'Primary worker' : selected ? 'Selected' : atCap ? 'Max 5 workers' : 'Add to job'}
                    >
                      {isPrimary && '★ '}
                      {w.name}
                    </button>
                  );
                })}
            </div>
          </Field>
        </div>
        <Field label="Description (visible to worker)">
          <textarea
            className={inputClass}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Field label="Internal notes (admin/dispatcher only)">
          <textarea
            className={inputClass}
            rows={3}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
          />
        </Field>
        <Field label="Reason for change (optional, written to audit log)">
          <input
            className={inputClass}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. customer requested reschedule"
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="text-sm text-stone-500 hover:underline">
            Cancel
          </button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving�' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

const STATUS_TRANSITIONS: Record<string, { label: string; to: string; needsReason?: boolean; danger?: boolean }[]> = {
  scheduled:   [{ label: 'Put on hold', to: 'on_hold', needsReason: true }, { label: 'Cancel job', to: 'cancelled', needsReason: true, danger: true }],
  assigned:    [{ label: 'Put on hold', to: 'on_hold', needsReason: true }, { label: 'Cancel job', to: 'cancelled', needsReason: true, danger: true }],
  accepted:    [{ label: 'Put on hold', to: 'on_hold', needsReason: true }, { label: 'Cancel job', to: 'cancelled', needsReason: true, danger: true }],
  on_my_way:   [{ label: 'Mark complete', to: 'completed' }, { label: 'Cancel job', to: 'cancelled', needsReason: true, danger: true }],
  in_progress: [{ label: 'Mark complete', to: 'completed' }, { label: 'Put on hold', to: 'on_hold', needsReason: true }],
  on_hold:     [{ label: 'Resume (assigned)', to: 'assigned' }, { label: 'Cancel job', to: 'cancelled', needsReason: true, danger: true }],
  completed:   [],
  cancelled:   [{ label: 'Reopen as scheduled', to: 'scheduled', needsReason: true }],
  draft:       [{ label: 'Schedule', to: 'scheduled' }],
};

function StatusActions({
  status,
  disabled,
  onChange,
  onReschedule,
}: {
  status: string;
  disabled: boolean;
  onChange: (status: string, reason?: string) => Promise<void>;
  onReschedule?: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const transitions = STATUS_TRANSITIONS[status] ?? [];

  if (transitions.length === 0 && !onReschedule) {
    return <p className="text-sm text-stone-500">No further status changes available from <strong>{status}</strong>.</p>;
  }

  async function go(to: string, needsReason?: boolean) {
    setError(null);
    let reason: string | undefined;
    if (needsReason) {
      const r = window.prompt('Reason (written to audit log):', '');
      if (r === null) return; // user cancelled
      reason = r.trim() || undefined;
    }
    setBusy(to);
    try {
      await onChange(to, reason);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {status === 'on_hold' && (
        <p className="text-xs text-stone-500">
          This job is on hold. Customers and workers cannot reschedule on-hold jobs — only admins can. Use <strong>Reschedule</strong> to pick a new date/time and reassign workers.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {onReschedule && (
          <Button
            variant="primary"
            disabled={disabled || busy !== null}
            onClick={onReschedule}
          >
            📅 Reschedule
          </Button>
        )}
        {transitions.map((t) => (
          <Button
            key={t.to}
            variant={t.danger ? 'danger' : 'white'}
            disabled={disabled || busy !== null}
            onClick={() => void go(t.to, t.needsReason)}
          >
            {busy === t.to ? 'Working…' : t.label}
          </Button>
        ))}
      </div>
      {error && (
        <p className="text-xs" style={{ color: BRAND.danger }}>{error}</p>
      )}
    </div>
  );
}

function RescheduleModal({
  open,
  onClose,
  job,
  workers,
  submitting,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  job: {
    scheduledAt: string;
    anytime?: boolean;
    assignedWorkerId?: string;
    assignedWorkerIds?: string[];
  };
  workers: { id: string; name: string; active: boolean }[];
  submitting: boolean;
  onSave: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [scheduledAt, setScheduledAt] = useState('');
  const [anytime, setAnytime] = useState(false);
  const [reason, setReason] = useState('');
  const [assignedWorkerIds, setAssignedWorkerIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever modal opens.
  useEffect(() => {
    if (!open) return;
    setScheduledAt(isoToDubaiLocal(job.scheduledAt));
    setAnytime(job.anytime ?? false);
    setAssignedWorkerIds(
      job.assignedWorkerIds && job.assignedWorkerIds.length > 0
        ? job.assignedWorkerIds
        : job.assignedWorkerId
          ? [job.assignedWorkerId]
          : [],
    );
    setReason('');
    setError(null);
  }, [open, job]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const localValue = anytime ? `${scheduledAt.slice(0, 10)}T08:00` : scheduledAt;
      const newIso = dubaiLocalToISO(localValue);
      // Resume into 'assigned' if any worker is on the job, else 'scheduled'.
      const nextStatus = assignedWorkerIds.length > 0 ? 'assigned' : 'scheduled';
      const body: Record<string, unknown> = {
        scheduledAt: newIso,
        anytime,
        assignedWorkerIds,
        status: nextStatus,
      };
      if (reason.trim()) body.reason = reason.trim();
      await onSave(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Reschedule job">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <p className="text-xs text-stone-500">
          Pick a new date/time and confirm the team. Saving resumes the job
          (status → <strong>assigned</strong> if any worker is selected,
          otherwise <strong>scheduled</strong>).
        </p>
        {error && (
          <div
            role="alert"
            className="rounded border px-3 py-2 text-xs"
            style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
          >
            {error}
          </div>
        )}
        <label
          className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2.5"
          style={{
            borderColor: anytime ? BRAND.primary : BRAND.border,
            backgroundColor: anytime ? '#F0FDF4' : 'white',
          }}
        >
          <span>
            <span className="text-sm font-medium" style={{ color: BRAND.text }}>Anytime</span>
            <span className="block text-xs text-stone-500">
              Customer is flexible / home all day — no specific time slot
            </span>
          </span>
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={anytime}
            onChange={(e) => setAnytime(e.target.checked)}
          />
        </label>
        <Field label={anytime ? 'New day' : 'New date & time'} hint={anytime ? 'Pick the day — the time is ignored.' : undefined}>
          <input
            className={inputClass}
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            required
          />
        </Field>
        <Field
          label="Workers"
          hint={`First selected = primary. Max 5.${assignedWorkerIds.length > 0 ? ` ${assignedWorkerIds.length} selected.` : ''}`}
        >
          <div className="flex flex-wrap gap-1.5">
            {workers
              .filter((w) => w.active || assignedWorkerIds.includes(w.id))
              .map((w) => {
                const idx = assignedWorkerIds.indexOf(w.id);
                const selected = idx !== -1;
                const isPrimary = idx === 0;
                const atCap = !selected && assignedWorkerIds.length >= 5;
                return (
                  <button
                    key={w.id}
                    type="button"
                    disabled={atCap}
                    onClick={() => {
                      setAssignedWorkerIds((cur) =>
                        selected
                          ? cur.filter((id) => id !== w.id)
                          : cur.length >= 5
                            ? cur
                            : [...cur, w.id],
                      );
                    }}
                    className="rounded-full px-3 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                    style={{
                      backgroundColor: selected ? BRAND.primary : 'white',
                      color: selected ? 'white' : BRAND.text,
                      border: `1px solid ${selected ? BRAND.primary : BRAND.border}`,
                    }}
                    title={isPrimary ? 'Primary worker' : selected ? 'Selected' : atCap ? 'Max 5 workers' : 'Add to job'}
                  >
                    {isPrimary && '★ '}
                    {w.name}
                  </button>
                );
              })}
          </div>
        </Field>
        <Field label="Reason (optional, written to audit log)">
          <input
            className={inputClass}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. customer requested next Tuesday"
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-stone-500 hover:underline">
            Cancel
          </button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Reschedule'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ShareMediaModal({
  open,
  onClose,
  jobId,
  serviceType,
  customerName,
  customerPhone,
}: {
  open: boolean;
  onClose: () => void;
  jobId: string;
  serviceType: string;
  customerName: string;
  customerPhone: string;
}) {
  const [items, setItems] = useState<JobProof[] | null>(null);
  const [shortByProof, setShortByProof] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [locale, setLocale] = useState<'en' | 'ar'>('en');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch fresh proof metadata + mint short branded URLs (1 hour TTL) when
  // the modal opens. Short URLs replace the 1500-char signed S3 URLs in the
  // outbound WhatsApp message and stay valid even if the underlying S3
  // presign needs to be regenerated when the customer taps the link.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProofForShare(jobId, 3600)
      .then(async (arr) => {
        if (cancelled) return;
        setItems(arr);
        setSelected(new Set(arr.filter((p) => p.kind !== 'signature').map((p) => p.id)));
        if (arr.length > 0) {
          try {
            const links = await createShareLinks(jobId, arr.map((p) => p.id), 3600);
            if (cancelled) return;
            const map: Record<string, string> = {};
            for (const l of links) map[l.proofId] = l.shortUrl;
            setShortByProof(map);
          } catch (err) {
            if (!cancelled) setError(err instanceof Error ? err.message : String(err));
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, jobId]);

  function toggle(id: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (!items) return;
    setSelected(new Set(items.map((p) => p.id)));
  }
  function selectNone() {
    setSelected(new Set());
  }

  function buildLink(): string | null {
    if (!items) return null;
    const chosen = items.filter((p) => selected.has(p.id));
    if (chosen.length === 0) return null;
    // Require short URLs for every chosen item before allowing send so the
    // customer never sees a raw S3 presign in chat.
    if (chosen.some((p) => !shortByProof[p.id])) return null;
    const lines = chosen.map((p, i) => {
      const label = KIND_LABELS[p.kind];
      const captionPart = p.caption ? ` — ${p.caption}` : '';
      return `${i + 1}. ${label}${captionPart}\n${shortByProof[p.id]}`;
    });
    return buildWhatsAppLink(
      customerPhone,
      'customer_job_media',
      {
        customerName,
        serviceType,
        mediaList: lines.join('\n\n'),
      },
      locale,
    );
  }

  const link = buildLink();

  return (
    <Modal open={open} onClose={onClose} title="Send media to customer">
      <div className="flex flex-col gap-4">
        <p className="text-xs text-stone-500">
          Pick the photos / videos to share. WhatsApp opens with a pre-filled
          message containing 1-hour preview links — nothing is sent
          automatically; tap <strong>Send</strong> in WhatsApp.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded border px-3 py-2 text-xs"
            style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
          >
            {error}
          </div>
        )}

        {loading && <p className="text-sm text-stone-500">Loading media…</p>}

        {items && items.length === 0 && (
          <p className="text-sm text-stone-500">No media on this job yet.</p>
        )}

        {items && items.length > 0 && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="text-stone-500">
                {selected.size} of {items.length} selected
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={selectAll} className="text-stone-700 hover:underline">
                  Select all
                </button>
                <button type="button" onClick={selectNone} className="text-stone-700 hover:underline">
                  None
                </button>
              </div>
            </div>

            <ul className="max-h-64 space-y-1 overflow-y-auto rounded border border-stone-200 bg-stone-50 p-2">
              {items.map((p) => {
                const isVideo = p.kind === 'video' || p.kind === 'intake_video';
                return (
                  <li key={p.id} className="flex items-center gap-2 rounded bg-white px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggle(p.id)}
                      className="h-4 w-4"
                    />
                    <span className="text-base">{isVideo ? '🎥' : '🖼️'}</span>
                    <span className="flex-1 truncate text-xs text-stone-700">
                      <strong>{KIND_LABELS[p.kind]}</strong>
                      {p.caption && <span className="text-stone-500"> · {p.caption}</span>}
                      <span className="text-stone-400"> · {fmt(p.uploadedAt)}</span>
                    </span>
                  </li>
                );
              })}
            </ul>

            <Field label="Language">
              <div className="flex gap-2">
                {(['en', 'ar'] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLocale(l)}
                    className="rounded-full px-3 py-1 text-xs font-medium transition"
                    style={{
                      backgroundColor: locale === l ? BRAND.primary : 'white',
                      color: locale === l ? 'white' : BRAND.text,
                      border: `1px solid ${locale === l ? BRAND.primary : BRAND.border}`,
                    }}
                  >
                    {l === 'en' ? 'English' : 'العربية'}
                  </button>
                ))}
              </div>
            </Field>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="text-sm text-stone-500 hover:underline">
            Cancel
          </button>
          {link ? (
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white shadow-sm"
              style={{ backgroundColor: '#25D366' }}
            >
              Open WhatsApp
            </a>
          ) : (
            <Button disabled>Open WhatsApp</Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function fmtAed(fils: number): string {
  return (fils / 100).toLocaleString('en-AE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function InvoicesPanel({
  jobId,
  customerName,
  customerPhone,
  serviceType,
}: {
  jobId: string;
  customerName?: string;
  customerPhone?: string;
  serviceType: string;
}) {
  const list = useJobInvoices(jobId);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (list.isPending) return <p className="text-sm text-stone-500">Loading invoices…</p>;
  if (list.error)
    return (
      <p className="text-sm" style={{ color: BRAND.danger }}>
        Failed to load invoices: {(list.error as Error).message}
      </p>
    );
  const items = list.data ?? [];
  if (items.length === 0)
    return (
      <p className="rounded-lg border-2 border-dashed border-stone-200 bg-stone-50 px-4 py-6 text-center text-sm text-stone-500">
        No invoices yet. Click <strong>Create invoice</strong> to generate one.
      </p>
    );

  async function shareToWhatsApp(inv: Invoice) {
    if (!customerPhone || !customerName) return;
    setBusyId(inv.id);
    try {
      // Re-fetch a long-lived presigned URL right before opening WhatsApp.
      const fresh = await fetchInvoiceShareUrl(inv.id);
      const link = buildWhatsAppLink(
        customerPhone,
        'customer_invoice',
        {
          customerName,
          serviceType,
          invoiceNumber: fresh.number,
          invoiceTotal: `AED ${fmtAed(fresh.totalFils)}`,
          invoiceUrl: fresh.shortUrl ?? fresh.url,
        },
        'en',
      );
      window.open(link, '_blank', 'noopener');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <ul className="space-y-2">
      {items.map((inv) => (
        <li
          key={inv.id}
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
        >
          <div className="min-w-0">
            <p className="font-semibold" style={{ color: BRAND.primary }}>
              {inv.number}{' '}
              <span className="ml-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                style={{ backgroundColor: BRAND.surfaceMuted, color: BRAND.text }}>
                {inv.status}
              </span>
            </p>
            <p className="text-xs text-stone-500">
              {new Date(inv.createdAt).toLocaleString('en-AE', {
                timeZone: 'Asia/Dubai',
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
              {' · '}
              {inv.items.length} {inv.items.length === 1 ? 'item' : 'items'}
              {' · '}VAT {(inv.vatRate * 100).toFixed(0)}%
            </p>
          </div>
          <div className="text-right">
            <p className="font-semibold" style={{ color: BRAND.primary }}>
              AED {fmtAed(inv.totalFils)}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <a
              href={inv.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border px-2.5 py-1 text-xs font-medium text-stone-700 hover:bg-stone-50"
              style={{ borderColor: BRAND.border }}
            >
              📄 PDF
            </a>
            {customerPhone && (
              <button
                type="button"
                disabled={busyId === inv.id}
                onClick={() => void shareToWhatsApp(inv)}
                className="rounded-md px-2.5 py-1 text-xs font-medium text-white shadow-sm disabled:opacity-50"
                style={{ backgroundColor: '#25D366' }}
              >
                {busyId === inv.id ? '…' : '📤 WhatsApp'}
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

interface DraftLine {
  description: string;
  quantity: string;
  unitPriceAed: string;
}

function CreateInvoiceModal({
  open,
  onClose,
  jobId,
}: {
  open: boolean;
  onClose: () => void;
  jobId: string;
}) {
  const [lines, setLines] = useState<DraftLine[]>([
    { description: '', quantity: '1', unitPriceAed: '' },
  ]);
  const [vatPct, setVatPct] = useState('5');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateInvoice(jobId);

  useEffect(() => {
    if (open) {
      setLines([{ description: '', quantity: '1', unitPriceAed: '' }]);
      setVatPct('5');
      setNotes('');
      setError(null);
    }
  }, [open]);

  function updateLine(i: number, patch: Partial<DraftLine>) {
    setLines((cur) => cur.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((cur) => [...cur, { description: '', quantity: '1', unitPriceAed: '' }]);
  }
  function removeLine(i: number) {
    setLines((cur) => (cur.length === 1 ? cur : cur.filter((_, idx) => idx !== i)));
  }

  const parsed = lines.map((l) => {
    const qty = parseFloat(l.quantity || '0');
    const unitFils = Math.round(parseFloat(l.unitPriceAed || '0') * 100);
    const lineFils = Math.round(qty * unitFils);
    return { description: l.description, qty, unitFils, lineFils };
  });
  const subtotalFils = parsed.reduce((s, p) => s + (Number.isFinite(p.lineFils) ? p.lineFils : 0), 0);
  const vatRate = (parseFloat(vatPct || '0') || 0) / 100;
  const vatFils = Math.round(subtotalFils * vatRate);
  const totalFils = subtotalFils + vatFils;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const cleanItems: InvoiceCreateBody['items'] = [];
    for (const p of parsed) {
      if (!p.description.trim()) continue;
      if (!Number.isFinite(p.qty) || p.qty <= 0) {
        setError('Quantity must be greater than zero on every row.');
        return;
      }
      if (!Number.isFinite(p.unitFils) || p.unitFils < 0) {
        setError('Unit price must be a non-negative number.');
        return;
      }
      cleanItems.push({
        description: p.description.trim(),
        quantity: p.qty,
        unitPriceFils: p.unitFils,
      });
    }
    if (cleanItems.length === 0) {
      setError('Add at least one line item with a description.');
      return;
    }

    try {
      await create.mutateAsync({
        items: cleanItems,
        notes: notes.trim() || undefined,
        vatRate,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Create invoice">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <p className="text-xs text-stone-500">
          Enter line items in <strong>AED</strong>. VAT applies on the subtotal.
          A PDF is generated and stored against this job.
        </p>

        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 items-end gap-2">
              <div className="col-span-6">
                <label className="block text-[11px] font-medium text-stone-600">
                  Description
                </label>
                <input
                  type="text"
                  value={l.description}
                  onChange={(e) => updateLine(i, { description: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. Labour — water heater repair"
                  maxLength={500}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[11px] font-medium text-stone-600">Qty</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={l.quantity}
                  onChange={(e) => updateLine(i, { quantity: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="col-span-3">
                <label className="block text-[11px] font-medium text-stone-600">
                  Unit (AED)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={l.unitPriceAed}
                  onChange={(e) => updateLine(i, { unitPriceAed: e.target.value })}
                  className={inputClass}
                  placeholder="0.00"
                />
              </div>
              <div className="col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeLine(i)}
                  disabled={lines.length === 1}
                  title="Remove row"
                  className="h-9 rounded-md border px-2 text-stone-500 hover:bg-stone-50 disabled:opacity-30"
                  style={{ borderColor: BRAND.border }}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addLine}
            className="text-xs font-medium hover:underline"
            style={{ color: BRAND.primary }}
          >
            + Add line
          </button>
        </div>

        <Field label="VAT %">
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={vatPct}
            onChange={(e) => setVatPct(e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Notes (optional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={inputClass}
            rows={2}
            maxLength={1000}
            placeholder="Anything you'd like the customer to see on the PDF…"
          />
        </Field>

        <div className="rounded-md border bg-stone-50 p-3 text-sm" style={{ borderColor: BRAND.border }}>
          <div className="flex justify-between text-stone-700">
            <span>Subtotal</span>
            <span>AED {fmtAed(subtotalFils)}</span>
          </div>
          <div className="flex justify-between text-stone-700">
            <span>VAT ({vatPct || 0}%)</span>
            <span>AED {fmtAed(vatFils)}</span>
          </div>
          <div className="mt-1 flex justify-between border-t pt-1 font-semibold" style={{ borderColor: BRAND.border, color: BRAND.primary }}>
            <span>Total</span>
            <span>AED {fmtAed(totalFils)}</span>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded border px-3 py-2 text-xs"
            style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm text-stone-500 hover:underline">
            Cancel
          </button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Generating…' : 'Generate invoice'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
